import React, { useMemo } from 'react';
import styled from 'styled-components';
import { Link, useLocation } from 'react-router-dom';
import { getAppProjectBySlug } from '../../data/apps';
import { useLanguage } from '../../i18n';

const Page = styled.div`
  padding-top: 110px;
  padding-bottom: 80px;
  background: ${props => props.theme.colors.background};
`;

const Container = styled.div`
  max-width: 960px;
  margin: 0 auto;
  padding: 0 24px;
`;

const Title = styled.h1`
  font-size: 34px;
  font-weight: 900;
  color: ${props => props.theme.colors.textDark};
  margin-bottom: 10px;
`;

const Meta = styled.p`
  color: ${props => props.theme.colors.textLight};
  margin-bottom: 26px;
`;

const Card = styled.div`
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.xl};
  padding: 22px;
  background: ${props => props.theme.colors.white};
  box-shadow: ${props => props.theme.shadows.sm};
`;

const H2 = styled.h2`
  font-size: 16px;
  font-weight: 900;
  margin: 18px 0 10px;
  color: ${props => props.theme.colors.textDark};
`;

const P = styled.p`
  line-height: 1.85;
  color: ${props => props.theme.colors.text};
  margin: 10px 0;
`;

const List = styled.ul`
  margin: 8px 0 8px 18px;
  line-height: 1.85;
  color: ${props => props.theme.colors.text};
`;

function useQuery() {
  const location = useLocation();
  return useMemo(() => new URLSearchParams(location.search), [location.search]);
}

const TermsOfUsePage: React.FC = () => {
  const { t } = useLanguage();
  const query = useQuery();
  const slug = query.get('app') ?? undefined;
  const app = getAppProjectBySlug(slug);
  const updatedAt = '2024-07-01';
  const effectiveAt = '2024-07-01';

  return (
    <Page>
      <Container>
        <Title>{t('legal.terms')}</Title>
        <Meta>
          {app ? (
            <>
              {t('legal.appliesTo')}: <strong>{app.name}</strong> ·{' '}
            </>
          ) : null}
          更新日期：{updatedAt} · 生效日期：{effectiveAt} · <Link to="/">{t('legal.backHome')}</Link>
        </Meta>

        <Card>
          <P>
            欢迎你使用「熊猫陪伴（PandaSpeak）」（以下简称“本应用”）。在使用本应用前，请你仔细阅读本用户条款（以下简称“条款”）。
            你下载、安装、访问或使用本应用，即表示你已阅读、理解并同意受本条款约束。
          </P>

          <H2>1. 服务内容</H2>
          <P>
            本应用提供语音互动与内容服务，包括但不限于语音对话、故事内容生成/播放、图片相关功能等。我们可能会根据产品迭代调整功能与服务形式。
          </P>

          <H2>2. 订阅与自动续期</H2>
          <List>
            <li>订阅属于自动续期服务。确认购买后，将从你的 Apple ID 账户扣费。</li>
            <li>订阅将自动续期，除非你在当前周期结束至少 24 小时前取消。</li>
            <li>续期费用将在当前周期结束前 24 小时内收取。</li>
            <li>你可以在 iOS 系统的“订阅”管理中查看、取消订阅。</li>
          </List>

          <H2>3. 使用规范</H2>
          <List>
            <li>你应遵守适用法律法规，不得利用本应用从事违法、侵权或破坏性行为。</li>
            <li>你对自己提交/输入/上传的内容负责，并确保拥有相应权利或授权。</li>
            <li>禁止对本应用进行逆向工程、破解、绕过付费机制或干扰服务稳定性。</li>
          </List>

          <H2>4. 知识产权</H2>
          <P>
            本应用及其相关内容（包括但不限于界面、标识、文本、图像、程序与服务）受法律保护。未经许可，不得复制、传播、出售或用于商业用途。
          </P>

          <H2>5. 免责声明</H2>
          <P>
            在法律允许的最大范围内，本应用按“现状”提供。我们不保证服务始终不中断、无错误或完全满足你的期望。
            因不可抗力、网络故障或第三方原因导致的服务中断，我们在法律允许范围内不承担责任。
          </P>

          <H2>6. 条款变更与终止</H2>
          <P>
            我们可能会不时更新本条款。更新后将发布在本页面并注明更新日期。若你在更新后继续使用本应用，则视为你接受更新后的条款。
          </P>

          <H2>7. Apple 标准最终用户许可协议（Standard EULA）</H2>
          <P>如你未单独设置自定义 EULA，本应用适用 Apple 标准最终用户许可协议。你可以通过下方链接查看：</P>
          <P>
            <a
              href="https://www.apple.com/legal/internet-services/itunes/dev/stdeula/"
              target="_blank"
              rel="noopener noreferrer"
            >
              https://www.apple.com/legal/internet-services/itunes/dev/stdeula/
            </a>
          </P>

          <H2>8. 联系我们</H2>
          <P>
            如你对本条款有任何问题，请联系：{' '}
            <a href="mailto:xbmld1@gmail.com">xbmld1@gmail.com</a>
          </P>

          <P style={{ marginTop: 16 }}>
            <Link to="/privacy">{t('legal.privacy')}</Link>
          </P>
        </Card>
      </Container>
    </Page>
  );
};

export default TermsOfUsePage;

